import { useAuth } from "../contexts/AuthContext";
import type { PermissionKey } from "../lib/permissions";

interface UsePermissionsReturn {
  /** `true` si el usuario actual puede realizar la acción asociada a `perm`. */
  can: (perm: PermissionKey) => boolean;
  /** `true` si el usuario tiene AL MENOS UNO de los permisos indicados. */
  canAny: (perms: PermissionKey[]) => boolean;
  /** `true` si el usuario tiene TODOS los permisos indicados. */
  canAll: (perms: PermissionKey[]) => boolean;
}

/**
 * Hook de permisos configurables por staff (FASE 3a — fundación).
 *
 * `owner`/`admin` tienen SIEMPRE acceso total (defensa en el propio hook, aunque
 * `user.permissions` llegara vacío o desactualizado): el backend ya les resuelve
 * las 16 claves en `/auth/me`, pero no dependemos de eso para no romper la UI si
 * ese contrato cambiara. Para `staff`, se comprueba `user.permissions` (el set
 * EFECTIVO que manda el backend — ya incluye los permisos por defecto si el
 * empleado no tiene configuración explícita).
 *
 * Este hook es SOLO para pintar la UI (ocultar botones, deshabilitar acciones).
 * El backend sigue siendo la fuente de verdad de la autorización real.
 *
 * NO usar todavía en gates de páginas concretas (eso es otra fase); de momento
 * es la fundación para que las siguientes fases lo consuman.
 */
export function usePermissions(): UsePermissionsReturn {
  const { user } = useAuth();

  const hasFullAccess = user?.role === "owner" || user?.role === "admin";
  const userPermissions = user?.permissions ?? [];

  const can = (perm: PermissionKey): boolean =>
    hasFullAccess || userPermissions.includes(perm);

  const canAny = (perms: PermissionKey[]): boolean =>
    hasFullAccess || perms.some((perm) => userPermissions.includes(perm));

  const canAll = (perms: PermissionKey[]): boolean =>
    hasFullAccess || perms.every((perm) => userPermissions.includes(perm));

  return { can, canAny, canAll };
}
